/**
 * 一時的制約を取得・適用するカスタムフック
 *
 * 制約設定ファイルから現在の商品タイプに応じた制約情報を提供し、
 * 必要に応じて自動選択を行います。
 */

import { useEffect } from 'react';
import { useOrderFlowStore } from '../store/orderFlowStore';
import { useTranslations } from './useTranslations';
import {
  TEMPORARY_RESTRICTIONS,
  type TemporaryRestrictions,
} from '../config/temporaryRestrictions';
import type { ProductType } from '../types/orderFlow';

/**
 * 商品タイプに応じた制約設定を取得
 */
const getRestrictionsForProductType = (
  productType: ProductType | null,
): TemporaryRestrictions['freezeDried'] | TemporaryRestrictions['liquidConcentrate'] | TemporaryRestrictions['straightCoffee'] | null => {
  switch (productType) {
    case 'freeze-dried':
      return TEMPORARY_RESTRICTIONS.freezeDried;
    case 'liquid-concentrate':
      return TEMPORARY_RESTRICTIONS.liquidConcentrate;
    case 'straight-coffee':
      return TEMPORARY_RESTRICTIONS.straightCoffee;
    default:
      return null;
  }
};

/**
 * 一時的制約情報を提供するフック
 */
export const useTemporaryRestrictions = () => {
  const { productType } = useOrderFlowStore();
  const t = useTranslations();

  const restrictions = getRestrictionsForProductType(productType);

  // 非活性メッセージを取得
  const disabledMessage = t.temporaryUnavailable_message || '現在選択できません';

  return {
    // パッキング種類の制約
    isSpoutDisabled:
      productType === 'liquid-concentrate' &&
      (restrictions as TemporaryRestrictions['liquidConcentrate'])?.disableSpout,
    isBottleDisabled:
      productType === 'straight-coffee' &&
      (restrictions as TemporaryRestrictions['straightCoffee'])?.disableBottle,

    // 砂糖の制約
    isSugarDisabled:
      productType === 'straight-coffee' &&
      (restrictions as TemporaryRestrictions['straightCoffee'])?.disableSugar,

    // パッケージオプションの制約
    isInnerDesignRequiredDisabled:
      productType === 'freeze-dried' &&
      (restrictions as TemporaryRestrictions['freezeDried'])?.disableInnerDesignRequired,
    isOuterBoxRequiredDisabled: restrictions
      ? 'disableOuterBoxRequired' in restrictions && restrictions.disableOuterBoxRequired
      : false,
    isFrontLabelDisabled:
      productType === 'liquid-concentrate'
        ? (restrictions as TemporaryRestrictions['liquidConcentrate'])?.disableFrontLabelSelection
        : productType === 'straight-coffee'
        ? (restrictions as TemporaryRestrictions['straightCoffee'])?.disableFrontLabelSelection
        : productType === 'freeze-dried'
        ? (restrictions as TemporaryRestrictions['freezeDried'])?.disableFrontLabelSelection
        : false,

    // 裏ラベル「必要」の制約
    isBackLabelRequiredDisabled: TEMPORARY_RESTRICTIONS.disableBackLabelRequired,

    // 非活性メッセージ
    disabledMessage,
  };
};

/**
 * 制約に基づく自動選択を適用するフック
 * SelectCoffeeBean と SelectPackageOptions で使用
 */
export const useApplyRestrictionDefaults = () => {
  const {
    productType,
    packageType,
    sugarConcentration,
    setPackageType,
    setSugarConcentration,
    setNeedsInnerDesign,
    setNeedsOuterBox,
    userSelections,
  } = useOrderFlowStore();

  const {
    isSpoutDisabled,
    isSugarDisabled,
    isInnerDesignRequiredDisabled,
    isOuterBoxRequiredDisabled,
  } = useTemporaryRestrictions();

  // パッキング種類と砂糖の自動選択（SelectCoffeeBean用）
  useEffect(() => {
    if (!productType) return;

    // 濃縮液: スパウト非活性 → pouch自動選択
    if (productType === 'liquid-concentrate' && isSpoutDisabled) {
      if (!packageType) {
        setPackageType('pouch');
      }
    }

    // ストレートコーヒー: 砂糖非活性 → 無糖自動選択
    if (productType === 'straight-coffee' && isSugarDisabled) {
      if (!sugarConcentration) {
        setSugarConcentration('none');
      }
    }
  }, [
    productType,
    packageType,
    sugarConcentration,
    isSpoutDisabled,
    isSugarDisabled,
    setPackageType,
    setSugarConcentration,
  ]);

  // パッケージオプションの自動選択（SelectPackageOptions用）
  useEffect(() => {
    if (!productType) return;

    // フリーズドライ・スティック: インナーデザイン非活性 → 不要自動選択
    if (productType === 'freeze-dried' && packageType === 'stick') {
      if (isInnerDesignRequiredDisabled && !userSelections.hasSelectedInnerDesign) {
        setNeedsInnerDesign(false);
      }
    }

    // 全タイプ: 外箱非活性 → 不要自動選択
    if (isOuterBoxRequiredDisabled && !userSelections.hasSelectedOuterBox) {
      setNeedsOuterBox(false);
    }
  }, [
    productType,
    packageType,
    isInnerDesignRequiredDisabled,
    isOuterBoxRequiredDisabled,
    userSelections.hasSelectedInnerDesign,
    userSelections.hasSelectedOuterBox,
    setNeedsInnerDesign,
    setNeedsOuterBox,
  ]);
};
